import { useRef, useCallback, useState, useEffect, useMemo } from 'react';
import { Link } from '@tanstack/react-router';
import { useSiteSchema, useForms } from '@wordpuppi/shared';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

interface TeraEditorProps {
  content: string;
  onChange: (content: string) => void;
  siteSlug: string;
  placeholder?: string;
  className?: string;
}

interface Suggestion {
  label: string;
  insertText: string;
  type: 'variable' | 'field' | 'keyword';
  fieldType?: string;
}

interface BuiltinVar {
  label: string;
  type: 'variable';
  fields?: string[];
  isArray?: boolean;
}

// Built-in variables available in all Tera templates
const BUILTIN_VARS: BuiltinVar[] = [
  { label: 'site', type: 'variable', fields: ['title', 'description', 'url', 'language', 'author'] },
  { label: 'page', type: 'variable', fields: ['title', 'content', 'slug', 'status', 'created_at', 'updated_at'] },
  { label: 'posts', type: 'variable', isArray: true },
  { label: 'pages', type: 'variable', isArray: true },
  { label: 'tags', type: 'variable', isArray: true },
  { label: 'build', type: 'variable', fields: ['timestamp', 'environment'] },
  { label: 'root_path', type: 'variable' },
  { label: 'content_type', type: 'variable' },
  { label: 'pagination', type: 'variable', fields: ['page', 'per_page', 'total', 'total_pages'] },
];

// Post/page common fields
const POST_FIELDS = ['id', 'title', 'slug', 'content', 'excerpt', 'status', 'featured_image', 'created_at', 'updated_at', 'published_at'];

/**
 * Parse for loops to build a map of loop variable -> collection name.
 * e.g., "{% for coach in coaches %}" -> { coach: 'coaches' }
 */
function parseForLoops(content: string): Map<string, string> {
  const loopVars = new Map<string, string>();
  const forLoopRegex = /\{%\s*for\s+(\w+)\s+in\s+(\w+)\s*%\}/g;
  let match;
  while ((match = forLoopRegex.exec(content)) !== null) {
    loopVars.set(match[1], match[2]);
  }
  return loopVars;
}

/**
 * Parse cursor context to determine what suggestions to show.
 */
function getContext(content: string, cursorPos: number): {
  type: 'root' | 'field' | 'forLoop';
  prefix: string;
  parent?: string;
  hasAtTrigger?: boolean;
} {
  const before = content.slice(0, cursorPos);
  const fullContent = content;

  // Parse for loops to know loop variable -> collection mappings
  const loopVars = parseForLoops(fullContent);

  // Check if @ appears at end (trigger) - could be: @, word@, variable.field@
  if (before.endsWith('@')) {
    const beforeAt = before.slice(0, -1);

    // Check for field access: variable.field@ or variable.@
    const fieldMatch = beforeAt.match(/(\w+)\.(\w*)$/);
    if (fieldMatch) {
      const [, variable, fieldPrefix] = fieldMatch;
      // Check if variable is a loop var and map to collection's singular form
      const collection = loopVars.get(variable);
      const parent = collection
        ? (collection.endsWith('s') ? collection.slice(0, -1) : collection)
        : variable;
      return { type: 'field', parent, prefix: fieldPrefix, hasAtTrigger: true };
    }

    // Check for root variable: word@
    const wordMatch = beforeAt.match(/(\w*)$/);
    return { type: 'root', prefix: wordMatch?.[1] || '', hasAtTrigger: true };
  }

  // Check if after a dot (field access without @): variable.field
  const dotMatch = before.match(/(\w+)\.\s*(\w*)$/);
  if (dotMatch) {
    const [, variable, fieldPrefix] = dotMatch;
    // Check if variable is a loop var and map to collection's singular form
    const collection = loopVars.get(variable);
    const parent = collection
      ? (collection.endsWith('s') ? collection.slice(0, -1) : collection)
      : variable;
    return { type: 'field', parent, prefix: fieldPrefix || '' };
  }

  // Check if in a for loop declaration: {% for x in prefix
  const forMatch = before.match(/\{%\s*for\s+\w+\s+in\s+(\w*)$/);
  if (forMatch) {
    return { type: 'forLoop', prefix: forMatch[1] || '' };
  }

  // Default: root variable context
  const wordMatch = before.match(/(\w*)$/);
  return { type: 'root', prefix: wordMatch?.[1] || '' };
}

/**
 * Get caret coordinates relative to textarea for positioning dropdown.
 */
function getCaretCoordinates(element: HTMLTextAreaElement, position: number): { top: number; left: number } {
  const div = document.createElement('div');
  const style = getComputedStyle(element);

  // Copy styles
  const properties = [
    'fontFamily', 'fontSize', 'fontWeight', 'wordWrap', 'whiteSpace',
    'borderLeftWidth', 'borderTopWidth', 'paddingLeft', 'paddingTop', 'lineHeight'
  ];
  properties.forEach((prop) => {
    div.style[prop as any] = style.getPropertyValue(prop.replace(/([A-Z])/g, '-$1').toLowerCase());
  });

  div.style.position = 'absolute';
  div.style.visibility = 'hidden';
  div.style.whiteSpace = 'pre-wrap';
  div.style.width = `${element.clientWidth}px`;

  const text = element.value.substring(0, position);
  div.textContent = text;

  const span = document.createElement('span');
  span.textContent = element.value.substring(position) || '.';
  div.appendChild(span);

  document.body.appendChild(div);
  const { offsetTop, offsetLeft } = span;
  document.body.removeChild(div);

  return {
    top: offsetTop + parseInt(style.lineHeight || '20', 10),
    left: offsetLeft,
  };
}

/**
 * A code editor for Tera templates with autocomplete support.
 */
export function TeraEditor({
  content,
  onChange,
  siteSlug,
  placeholder = 'Enter Tera template... (Type @ for suggestions)',
  className = '',
}: TeraEditorProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [context, setContext] = useState<ReturnType<typeof getContext>>({ type: 'root', prefix: '' });

  // Fetch schema for custom types
  const { data: schemaData } = useSiteSchema(siteSlug);
  // Fetch forms for insert dialog
  const { data: formsData } = useForms(siteSlug);
  const [showFormsDialog, setShowFormsDialog] = useState(false);

  // Build suggestions from schema + built-ins
  const allSuggestions = useMemo(() => {
    const suggestions: Map<string, Suggestion[]> = new Map();

    // Root variables
    const rootSuggestions: Suggestion[] = BUILTIN_VARS.map((v) => ({
      label: v.label,
      insertText: v.label,
      type: 'variable' as const,
      fieldType: v.isArray ? 'array' : 'object',
    }));

    // Add custom types as root variables
    if (schemaData?.data?.content_types) {
      for (const ct of schemaData.data.content_types) {
        // Skip system types that are already covered
        if (['wpp_posts', 'wpp_pages', 'wpp_tags'].includes(ct.slug)) continue;

        // Strip wpp_ prefix for template variable name
        const varName = ct.slug.replace(/^wpp_/, '');
        rootSuggestions.push({
          label: varName,
          insertText: varName,
          type: 'variable',
          fieldType: 'array',
        });
      }
    }
    suggestions.set('root', rootSuggestions);

    // Fields for built-in variables
    for (const v of BUILTIN_VARS) {
      if (v.fields) {
        suggestions.set(v.label, v.fields.map((f) => ({
          label: f,
          insertText: f,
          type: 'field' as const,
        })));
      }
    }

    // Post fields (for items in posts array and for loop variables)
    suggestions.set('post', POST_FIELDS.map((f) => ({
      label: f,
      insertText: f,
      type: 'field' as const,
    })));

    // Page fields
    suggestions.set('page', POST_FIELDS.map((f) => ({
      label: f,
      insertText: f,
      type: 'field' as const,
    })));

    // Custom type fields
    if (schemaData?.data?.content_types) {
      for (const ct of schemaData.data.content_types) {
        const varName = ct.slug.replace(/^wpp_/, '');
        // Singular form for loop variable (coaches -> coach)
        const singular = varName.endsWith('s') ? varName.slice(0, -1) : varName;

        const fields: Suggestion[] = ct.fields?.map((f) => ({
          label: f.slug,
          insertText: f.slug,
          type: 'field' as const,
          fieldType: f.field_type,
        })) || [];

        suggestions.set(singular, fields);
        suggestions.set(varName, fields); // Also map plural for direct access
      }
    }

    return suggestions;
  }, [schemaData]);

  // Filter suggestions based on context
  const filteredSuggestions = useMemo(() => {
    let suggestions: Suggestion[] = [];

    if (context.type === 'root' || context.type === 'forLoop') {
      suggestions = allSuggestions.get('root') || [];
      // For loops should only show arrays
      if (context.type === 'forLoop') {
        suggestions = suggestions.filter((s) => s.fieldType === 'array');
      }
    } else if (context.type === 'field' && context.parent) {
      suggestions = allSuggestions.get(context.parent) || [];
    }

    // Filter by prefix
    if (context.prefix) {
      const lowerPrefix = context.prefix.toLowerCase();
      suggestions = suggestions.filter((s) =>
        s.label.toLowerCase().startsWith(lowerPrefix)
      );
    }

    return suggestions;
  }, [allSuggestions, context]);

  // Reset selection when suggestions change
  useEffect(() => {
    setSelectedIndex(0);
  }, [filteredSuggestions]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      onChange(e.target.value);
    },
    [onChange]
  );

  const openAutocomplete = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const cursorPos = textarea.selectionStart;
    const ctx = getContext(textarea.value, cursorPos);
    setContext(ctx);

    // Get dropdown position
    const coords = getCaretCoordinates(textarea, cursorPos);
    setDropdownPosition(coords);
    setShowAutocomplete(true);
    setSelectedIndex(0);
  }, []);

  const insertSuggestion = useCallback((suggestion: Suggestion) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const cursorPos = textarea.selectionStart;
    const before = content.slice(0, cursorPos);
    const after = content.slice(cursorPos);

    // Determine what to remove based on context
    let removeLength = context.prefix.length;
    if (context.hasAtTrigger) {
      // @ is at the end, so remove: prefix + @
      // For field access like "coach.la@" -> remove "la@", keep "coach."
      // For root like "si@" -> remove "si@"
      removeLength = context.prefix.length + 1; // +1 for the @
    }
    const newBefore = before.slice(0, before.length - removeLength);
    const newContent = newBefore + suggestion.insertText + after;

    onChange(newContent);
    setShowAutocomplete(false);

    // Move cursor after inserted text
    const newCursorPos = newBefore.length + suggestion.insertText.length;
    requestAnimationFrame(() => {
      textarea.selectionStart = textarea.selectionEnd = newCursorPos;
      textarea.focus();
    });
  }, [content, context.prefix, context.hasAtTrigger, onChange]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      // Ctrl+Space to open autocomplete
      if (e.key === ' ' && e.ctrlKey) {
        e.preventDefault();
        openAutocomplete();
        return;
      }

      // Handle autocomplete navigation when open
      if (showAutocomplete) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setSelectedIndex((i) => Math.min(i + 1, filteredSuggestions.length - 1));
          return;
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          setSelectedIndex((i) => Math.max(i - 1, 0));
          return;
        }
        if (e.key === 'Enter' || e.key === 'Tab') {
          if (filteredSuggestions.length > 0) {
            e.preventDefault();
            insertSuggestion(filteredSuggestions[selectedIndex]);
            return;
          }
        }
        if (e.key === 'Escape') {
          e.preventDefault();
          setShowAutocomplete(false);
          return;
        }
      }

      // Tab key inserts spaces
      if (e.key === 'Tab' && !showAutocomplete) {
        e.preventDefault();
        const textarea = textareaRef.current;
        if (!textarea) return;

        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const value = textarea.value;

        const newValue = value.substring(0, start) + '  ' + value.substring(end);
        onChange(newValue);

        requestAnimationFrame(() => {
          textarea.selectionStart = textarea.selectionEnd = start + 2;
        });
      }
    },
    [showAutocomplete, filteredSuggestions, selectedIndex, openAutocomplete, insertSuggestion, onChange]
  );

  // Open autocomplete on @ symbol
  const handleInput = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const cursorPos = textarea.selectionStart;
    const charBefore = textarea.value[cursorPos - 1];

    // Trigger on @ symbol
    if (charBefore === '@') {
      openAutocomplete();
    }
  }, [openAutocomplete]);

  // Close autocomplete when clicking outside
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (!(e.target as Element).closest('.tera-editor')) {
        setShowAutocomplete(false);
      }
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  const lineCount = content.split('\n').length;

  // Double-scrollbar fix: size the textarea to its content so only
  // `.tera-editor-container` scrolls (scrollHeight accounts for soft wrap).
  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = `${ta.scrollHeight}px`;
  }, [content]);
  const forms = formsData?.data ?? [];

  // Detect form markers in content: <!-- wpp:form slug="..." -->
  const detectedForms = useMemo(() => {
    const regex = /<!--\s*wpp:form\s+slug="([^"]+)"\s*-->/g;
    const found: Array<{ slug: string; line: number; formId?: string }> = [];
    let match;
    const lines = content.split('\n');

    lines.forEach((line, index) => {
      regex.lastIndex = 0;
      while ((match = regex.exec(line)) !== null) {
        const slug = match[1];
        const form = forms.find((f) => f.slug === slug);
        found.push({
          slug,
          line: index + 1,
          formId: form?.id,
        });
      }
    });

    return found;
  }, [content, forms]);

  const insertFormTag = useCallback((formSlug: string) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const cursorPos = textarea.selectionStart;
    const before = content.slice(0, cursorPos);
    const after = content.slice(cursorPos);

    const formTag = `<!-- wpp:form slug="${formSlug}" -->`;
    const newContent = before + formTag + after;

    onChange(newContent);
    setShowFormsDialog(false);

    const newCursorPos = cursorPos + formTag.length;
    requestAnimationFrame(() => {
      textarea.selectionStart = textarea.selectionEnd = newCursorPos;
      textarea.focus();
    });
  }, [content, onChange]);

  return (
    <div className={`tera-editor ${className}`}>
      <div className="tera-editor-header">
        <span className="tera-editor-label">Tera Template</span>
        <div className="tera-editor-actions">
          {forms.length > 0 && (
            <Dialog open={showFormsDialog} onOpenChange={setShowFormsDialog}>
              <DialogTrigger>
                <span className="inline-flex items-center justify-center h-6 px-2 text-xs rounded-md hover:bg-muted cursor-pointer">
                  Insert Form
                </span>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Insert Form</DialogTitle>
                </DialogHeader>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {forms.map((form) => (
                    <button
                      key={form.id}
                      type="button"
                      className="w-full text-left p-3 rounded-lg border hover:bg-muted transition-colors"
                      onClick={() => insertFormTag(form.slug)}
                    >
                      <div className="font-medium">{form.name}</div>
                      <div className="text-sm text-muted-foreground font-mono">
                        {form.slug}
                      </div>
                    </button>
                  ))}
                </div>
              </DialogContent>
            </Dialog>
          )}
          <span className="tera-editor-hint">
            Type @ for suggestions
          </span>
        </div>
      </div>
      <div className="tera-editor-container">
        <div className="tera-editor-lines">
          {Array.from({ length: Math.max(lineCount, 10) }, (_, i) => (
            <div key={i} className="tera-editor-line-number">
              {i + 1}
            </div>
          ))}
        </div>
        <div className="tera-editor-textarea-wrapper">
          <textarea
            ref={textareaRef}
            className="tera-editor-textarea"
            value={content}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onInput={handleInput}
            placeholder={placeholder}
            spellCheck={false}
            autoCapitalize="off"
            autoComplete="off"
            autoCorrect="off"
          />
          {showAutocomplete && filteredSuggestions.length > 0 && (
            <div
              className="tera-autocomplete"
              style={{
                top: dropdownPosition.top,
                left: dropdownPosition.left,
              }}
            >
              {filteredSuggestions.map((suggestion, index) => (
                <div
                  key={suggestion.label}
                  className={`tera-autocomplete-item ${index === selectedIndex ? 'selected' : ''}`}
                  onClick={() => insertSuggestion(suggestion)}
                  onMouseEnter={() => setSelectedIndex(index)}
                >
                  <span>{suggestion.label}</span>
                  {suggestion.fieldType && (
                    <span className="tera-autocomplete-type">{suggestion.fieldType}</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <div className="tera-editor-footer">
        <span>{lineCount} lines</span>
        <span>{content.length} chars</span>
      </div>
      {detectedForms.length > 0 && (
        <div className="tera-editor-forms">
          <span className="tera-editor-forms-label">Embedded Forms:</span>
          {detectedForms.map((df, idx) => (
            <span key={idx} className="tera-editor-form-badge">
              {df.formId ? (
                <Link
                  to="/sites/$siteSlug/forms/$formId"
                  params={{ siteSlug, formId: df.formId }}
                  className="tera-editor-form-link"
                >
                  {df.slug}
                </Link>
              ) : (
                <span className="tera-editor-form-missing">{df.slug} (not found)</span>
              )}
              <span className="tera-editor-form-line">L{df.line}</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
